import TransportWebUSB from '@ledgerhq/hw-transport-webusb';

// Waves Ledger app APDU layer (transport-agnostic).
// Implements the signing protocol expected by the deployed LedgerHQ Waves app
// (the "1.1" protocol: 28-byte prefix + tx bytes repeated twice).
const CLA = 0x80;
const INS_GET_PUBLIC_KEY = 0x04;
const INS_SIGN = 0x02;
const MAX_SIZE = 128;

export const WAVES_MAINNET_CODE = 0x57;
export const WAVES_TESTNET_CODE = 0x54;
export const DEFAULT_PATH = "44'/5741564'/0'/0'/0'";

const BASE58_ALPHABET =
  '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

export function base58Encode(buffer) {
  const bytes = Array.from(buffer);
  if (!bytes.length) return '';
  const digits = [0];
  for (let i = 0; i < bytes.length; i++) {
    for (let j = 0; j < digits.length; j++) digits[j] <<= 8;
    digits[0] += bytes[i];
    let carry = 0;
    for (let k = 0; k < digits.length; k++) {
      digits[k] += carry;
      carry = (digits[k] / 58) | 0;
      digits[k] %= 58;
    }
    while (carry) {
      digits.push(carry % 58);
      carry = (carry / 58) | 0;
    }
  }
  for (let i = 0; bytes[i] === 0 && i < bytes.length - 1; i++) digits.push(0);
  return digits.reverse().map((d) => BASE58_ALPHABET[d]).join('');
}

function toInt32Bytes(num) {
  const buf = new Uint8Array(4);
  new DataView(buf.buffer).setUint32(0, num, false); // big-endian
  return buf;
}

function splitPath(path) {
  const result = [];
  path.split('/').forEach((element) => {
    let number = parseInt(element, 10);
    if (Number.isNaN(number)) return;
    if (element.length > 1 && element[element.length - 1] === "'") {
      number += 0x80000000;
    }
    result.push(number);
  });
  const buffer = new Uint8Array(result.length * 4);
  const view = new DataView(buffer.buffer);
  result.forEach((element, index) => view.setUint32(4 * index, element, false));
  return buffer;
}

export function pathForAccount(index) {
  return `44'/5741564'/0'/0'/${index}'`;
}

const STATUS_TEXT = {
  '6985': 'Conditions not satisfied (you likely rejected the prompt on the device).',
  '6986': 'Device is locked — unlock it and retry.',
  '6982': 'Security status not satisfied.',
  '6a80': 'Invalid data sent to the device.',
  '6a86': 'Incorrect P1/P2 parameters.',
  '6d00': 'INS not supported — open the "Waves" app on the Ledger device.',
  '6e00': 'CLA not supported — the Waves app is not open on the device.',
  '6700': 'Wrong APDU length.',
  '9100': 'Transaction was cancelled on the device.',
};

function describeError(err) {
  const code = err && err.statusCode ? err.statusCode.toString(16) : null;
  const reason = code ? STATUS_TEXT[code] : null;
  if (reason) return new Error(`Ledger error 0x${code} — ${reason}`);
  return err;
}

function concatBytes(...arrays) {
  const total = arrays.reduce((s, a) => s + a.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const a of arrays) {
    out.set(a, offset);
    offset += a.length;
  }
  return out;
}

export class WavesLedger {
  constructor(networkCode = WAVES_MAINNET_CODE) {
    this.networkCode = networkCode;
    this.transport = null;
  }

  get connected() {
    return !!this.transport;
  }

  async connect() {
    // Try WebHID first (more reliable on Windows for Ledger, used by Ledger Live itself),
    // fall back to WebUSB.
    let lastErr;
    try {
      const TransportWebHID = (await import('@ledgerhq/hw-transport-webhid')).default;
      // open an already-approved device if possible, otherwise prompt the user
      let t = await TransportWebHID.openConnected();
      if (!t) t = await TransportWebHID.create();
      this.transport = t;
    } catch (e) {
      lastErr = e;
      try {
        this.transport = await TransportWebUSB.create();
      } catch (e2) {
        throw new Error(
          'Could not connect to the Ledger. ' +
          'Make sure the device is plugged in, unlocked, the Waves app is open, ' +
          'and that Ledger Live is closed (it can hold the device). ' +
          `Detail: ${(e2 && e2.message) || e2}`,
        );
      }
    }
    this.transport.setExchangeTimeout(300000);
    return this.transport;
  }

  async disconnect() {
    if (this.transport) {
      try {
        await this.transport.close();
      } catch {
        /* ignore */
      }
      this.transport = null;
    }
  }

  _requireTransport() {
    if (!this.transport) throw new Error('Ledger not connected. Click "Connect Ledger" first.');
  }

  async getWalletPublicKey(path, { verify = false } = {}) {
    this._requireTransport();
    const data = splitPath(path);
    const p1 = verify ? 0x80 : 0x00;
    try {
      const response = await this.transport.send(CLA, INS_GET_PUBLIC_KEY, p1, this.networkCode, data);
      const publicKeyBytes = response.slice(0, 32);
      const address = new TextDecoder().decode(response.slice(32, 32 + 35));
      return { publicKey: base58Encode(publicKeyBytes), address, path };
    } catch (err) {
      throw describeError(err);
    }
  }

  async signTransaction(path, txBytes, opts = {}) {
    this._requireTransport();
    const {
      dataType = 4,
      dataVersion = 2,
      amountPrecision = 8,
      feePrecision = 8,
    } = opts;

    // 1.1 sign protocol:
    // prefix = path(20) + [amount, fee, type, version](4) + dataLen(4) = 28 bytes
    // followed by the tx bytes repeated TWICE (signing hashes 2 passes).
    const prefix = concatBytes(
      splitPath(path),
      new Uint8Array([amountPrecision, feePrecision, dataType, dataVersion]),
      toInt32Bytes(txBytes.length),
    );
    const dataForDevice = concatBytes(prefix, txBytes, txBytes);

    const maxChunkLength = MAX_SIZE - 5;
    const dataLength = dataForDevice.length;
    let sendBytes = 0;
    let result = new Uint8Array(0);
    try {
      while (dataLength > sendBytes) {
        const chunkLength = Math.min(dataLength - sendBytes, maxChunkLength);
        const isLastByte = dataLength - sendBytes > maxChunkLength ? 0x00 : 0x80;
        const chunk = dataForDevice.slice(sendBytes, sendBytes + chunkLength);
        sendBytes += chunkLength;
        result = await this.transport.send(CLA, INS_SIGN, isLastByte, this.networkCode, chunk);
      }
      // transport returns response WITH 2 trailing status bytes — strip them
      return base58Encode(result.slice(0, result.length - 2));
    } catch (err) {
      throw describeError(err);
    }
  }
}
